import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import Preloader from "../shared/PreLoaders";
import { Authorization } from "../Context/Authorization";
import "./reporttable.css";
import axios from "axios";
import { AuthContext } from "../Context/AuthContext";
import RangeTableView from "./RangeTableView";
import ClosingBalance from "../components/CashBook/ClosingBalance";
import RangeApproval from "./RangeApproval";
import CircleApproval from "./CircleApproval";
import CircleStatusModal from "./CircleStatusModal";
import ServiceChargePaymentCheck from "./components/ServiceChargePaymentCheck";
import DashboardDataCard from "./components/DashboardDataCard"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import DivisionApproval from "./DivisionApproval";
import ViewBankBalance from "../components/CashBook/ViewBankBalance";
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const getDashboardCacheKey = (payload) => {
  const start = payload.startDate || "all";
  const end = payload.endDate || "all";

  return `dashboardData_${start}_${end}`;
};
const getCachedData = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  const { data, timestamp } = JSON.parse(cached);
  if (Date.now() - timestamp < CACHE_DURATION) {
    return data;
  }

  localStorage.removeItem(key); // remove expired cache
  return null;
};

const setCachedData = (key, data) => {
  localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
};
const SERVICE_CHARGE_EXCLUDED_ROLES = [
  "Admin",
  "Super Admin",
  "DFO",
  "CCF",
];

const CLOSING_BALANCE_EXCLUDED_ROLES = [
  "Admin",
  "Super Admin",
  "DFO",
  "CCF",
];

const Dashboard = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { role, permissions } = useContext(AuthContext);
  const [showClosingBalance, setShowClosingBalance] = useState(false);
  const [showRangeApproval, setShowRangeApproval] = useState(false);
  const navigate = useNavigate();
  const [showDivisionApproval, setShowDivisionApproval] = useState(false);
  const [showCircleApproval, setShowCircleApproval] = useState(false);
  const [showStateView, setShowStateView] = useState(false);
  const [selectedJfmcType, setSelectedJfmcType] = useState(null);
  const [vfcChartData, setVfcChartData] = useState([]);
  const [memberChartData, setMemberChartData] = useState([]);
  const [loanChartData, setLoanChartData] = useState([]);
  const [dashboardData, setDashboardData] = useState([]);
  const [serviceCheckDone, setServiceCheckDone] = useState(false);
  const [endDate, setEndDate] = useState("");
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (!serviceCheckDone) return;

    fetchDashboardData();
    loadVfcChart();
    loadMemberChart();
    loadLoanChart();
  }, [serviceCheckDone, startDate, endDate]);

  useEffect(() => {
    if (!Array.isArray(dashboardData) || dashboardData.length === 0) {
      return;
    }

    if (selectedJfmcType) {
      return;
    }

    // Only automatically select VFC for Admin / Super Admin
    const EXCLUDED_JFMC_SELECTION_ROLES = [
      "Admin",
      "Super Admin",
    ];

    if (EXCLUDED_JFMC_SELECTION_ROLES.includes(role)) {
      const vfc = dashboardData.find(
        (item) =>
          String(item?.jfmcTypeName || "").trim().toUpperCase() === "VFC"
      );

      if (vfc) {
        setSelectedJfmcType({
          _id: vfc.jfmcTypeId,
          jfmcTypeName: vfc.jfmcTypeName,
        });
      }
    }
  }, [dashboardData, selectedJfmcType, role]);
  // useEffect(() => {
  //   if (!serviceCheckDone) return;
  //   if (!selectedJfmcType?._id) return;
  //   if (selectedJfmcType?._id) {
  //     checkClosingBalance();
  //   }
  // }, [serviceCheckDone, selectedJfmcType]);

  useEffect(() => {
    if (!serviceCheckDone) return;
    if (!selectedJfmcType?._id) return;

    checkClosingBalance();
  }, [selectedJfmcType]);

  // const handleJfmcTypeSelect = (type) => {
  //   setSelectedJfmcType(type);

  // };


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      const config = Authorization();

      const headers = {
        "Content-Type": "application/json",
        ...config.headers,
      };

      // ==================================================
      // PAYLOAD
      // ==================================================

      const payload = {};

      if (startDate && endDate) {
        payload.startDate =
          startDate.toISOString().split("T")[0];

        payload.endDate =
          endDate.toISOString().split("T")[0];
      }
      const [
        basicResponse,
        loanResponse,
      ] = await Promise.all([
        fetch(
          `${Api}/api/all/dashboard-data`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }
        ),

        fetch(
          `${Api}/api/all/dashboard-data-loans`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          }
        ),
      ]);

      if (!basicResponse.ok) {
        throw new Error(
          "Failed to fetch dashboard basic data"
        );
      }

      if (!loanResponse.ok) {
        throw new Error(
          "Failed to fetch dashboard loan data"
        );
      }

      const basicData =
        await basicResponse.json();

      const loanData =
        await loanResponse.json();

      const basicArray =
        Array.isArray(basicData)
          ? basicData
          : [];

      const loanArray =
        Array.isArray(loanData)
          ? loanData
          : [];
      // ==================================================
      // STEP 2
      // BUILD LIVE DASHBOARD DATA
      // ==================================================

      const liveDashboardData =
        buildDashboardData(
          basicArray,
          loanArray
        );
      setDashboardData(
        liveDashboardData
      );

      setCachedData(
        "dashboardData",
        liveDashboardData
      );

      setLoading(false);
      const createResponse =
        await fetch(
          `${Api}/api/data`,
          {
            method: "POST",
            headers,
          }
        );

      if (!createResponse.ok) {
        throw new Error(
          "Failed to initialize DashboardData"
        );
      }

      const createResult =
        await createResponse.json();

      const staffId =
        createResult?.data?.staffId;

      if (!staffId) {
        throw new Error(
          "Staff ID not found in DashboardData response"
        );
      }

      // ==================================================
      // STEP 5
      // GET STORED DASHBOARD DATA
      // ==================================================
      const dashboardResponse =
        await fetch(
          `${Api}/api/data`,
          {
            method: "GET",
            headers,
          }
        );

      if (!dashboardResponse.ok) {
        const errorData =
          await dashboardResponse
            .json()
            .catch(() => ({}));

        throw new Error(
          errorData.message ||
          "Failed to fetch stored DashboardData"
        );
      }

      const dashboardResult =
        await dashboardResponse.json();

      const storedDashboard =
        dashboardResult?.data;

      const storedData =
        Array.isArray(
          storedDashboard?.dashboardData
        )
          ? storedDashboard.dashboardData
          : [];

      // ==================================================
      // STEP 6
      // IF DB DATA EXISTS
      // ==================================================

      if (storedData.length > 0) {
        const storedUIData =
          convertStoredDashboardToUI(
            storedData,
            basicArray
          );

        // ==================================================
        // SHOW DB DATA
        // ==================================================

        setDashboardData(
          storedUIData
        );
        setCachedData(
          "dashboardData",
          storedUIData
        );
        // ==================================================
        // STEP 7
        // BACKGROUND REFRESH
        // ==================================================

        reconcileDashboardDataInBackground({
          payload,
          headers,
          storedData,

          // IMPORTANT:
          // Reuse already fetched live data.
          liveBasicData: basicArray,
          liveLoanData: loanArray,
        });

        return;
      }

      // ==================================================
      // STEP 8
      // NO DB DATA
      // ==================================================

      console.log(
        "DashboardData is empty."
      );

      console.log(
        "Saving live loan/collection data in background..."
      );

      // We already displayed live basic + loan data.
      // Only collection needs to be fetched now.

      reconcileDashboardDataInBackground({
        payload,
        headers,
        storedData: [],

        liveBasicData: basicArray,
        liveLoanData: loanArray,
      });

    } catch (error) {
      setError(
        error.message ||
        "Failed to load dashboard"
      );
      setDashboardData([]);
      setLoading(false);
    }
  };
  const buildDashboardData = (
    basicArray,
    loanArray
  ) => {
    const loanMap = new Map(
      loanArray.map((item) => [
        String(item.jfmcTypeId),
        item,
      ])
    );

    return basicArray.map((basic) => {
      const jfmcTypeId =
        String(basic.jfmcTypeId);

      const loan =
        loanMap.get(jfmcTypeId) || {};

      const loanSummary = {};

      Object.entries(
        loan.loanSummary || {}
      ).forEach(([loanType, loanItem]) => {
        loanSummary[loanType] = {
          loanTypeId:
            loanItem.loanTypeId || null,

          loanName:
            loanItem.loanName ||
            loanType,

          amount:
            Number(
              loanItem.amount || 0
            ),

          count:
            Number(
              loanItem.count || 0
            ),

          collections: 0,

          collectionLoaded: false,
        };
      });

      return {
        ...basic,

        jfmcTypeId,

        loanSummary,

        totalLoanAmount:
          Number(
            loan.totalLoanAmount || 0
          ),

        totalCollectionAmount: 0,

        collectionLoaded: false,
      };
    });
  };
  const convertStoredDashboardToUI = (
    storedData,
    liveBasicData = []
  ) => {
    if (!Array.isArray(storedData)) {
      return [];
    }

    // ---------------------------------------------
    // Create lookup for live basic dashboard data
    // ---------------------------------------------
    const basicMap = new Map(
      liveBasicData.map((item) => [
        String(item.jfmcTypeId),
        item,
      ])
    );

    return storedData.map((item) => {
      const jfmcTypeId = String(
        item.jfmcTypeId
      );

      // ---------------------------------------------
      // Get live VFC / Member / SHG information
      // ---------------------------------------------
      const liveBasic =
        basicMap.get(jfmcTypeId) || {};

      const loanSummary = {};

      // ---------------------------------------------
      // LOANS FROM DashboardData
      // ---------------------------------------------
      (item.loans || []).forEach((loan) => {
        loanSummary[loan.loanName] = {
          loanTypeId:
            loan.loanTypeId,

          loanName:
            loan.loanName,

          amount:
            Number(loan.amount || 0),

          count:
            Number(loan.count || 0),

          collections: 0,

          collectionLoaded: false,
        };
      });

      // ---------------------------------------------
      // COLLECTIONS FROM DashboardData
      // ---------------------------------------------
      (item.loanCollections || []).forEach(
        (collection) => {
          const loanName =
            collection.loanName;

          if (!loanSummary[loanName]) {
            loanSummary[loanName] = {
              loanTypeId:
                collection.loanTypeId,

              loanName,

              amount: 0,

              count: 0,

              collections:
                Number(
                  collection.collected ||
                  0
                ),

              collectionLoaded: true,
            };
          } else {
            loanSummary[loanName] = {
              ...loanSummary[loanName],

              collections:
                Number(
                  collection.collected ||
                  0
                ),

              collectionLoaded: true,
            };
          }
        }
      );

      // ---------------------------------------------
      // RETURN MERGED UI DATA
      // ---------------------------------------------
      return {
        // First take live basic data
        ...liveBasic,

        // Then use DB JFMC information
        jfmcTypeId:
          item.jfmcTypeId,

        jfmcTypeName:
          item.jfmcTypeName,

        // Persisted loan data
        loanSummary,

        totalLoanAmount:
          Number(
            item.totalLoanAmount || 0
          ),

        totalCollectionAmount:
          Number(
            item.totalCollectionAmount || 0
          ),

        // Collection status
        collectionLoaded:
          (item.loanCollections || [])
            .length > 0,
      };
    });
  };

  const reconcileDashboardDataInBackground = async ({
    payload,
    headers,
    storedData,
    liveBasicData = [],
    liveLoanData = [],
    cacheKey,
  }) => {
    try {
      const latestData = buildDashboardData(
        Array.isArray(liveBasicData) ? liveBasicData : [],
        Array.isArray(liveLoanData) ? liveLoanData : []
      );

      // Keep existing loan values visible while collections load.

      setDashboardData((current) => {
        const currentMap = new Map(
          current.map((item) => [
            String(item.jfmcTypeId),
            item,
          ])
        );

        return latestData.map((item) => {
          const existing = currentMap.get(
            String(item.jfmcTypeId)
          );

          if (!existing) {
            return item;
          }

          const existingLoans = Object.values(
            existing.loanSummary || {}
          );

          const loanSummary = { ...item.loanSummary };

          Object.keys(loanSummary).forEach((key) => {
            const currentLoan = loanSummary[key];

            // Match by loanTypeId first, then by loan name.
            const previousLoan = existingLoans.find(
              (loan) =>
                currentLoan.loanTypeId &&
                loan.loanTypeId &&
                String(currentLoan.loanTypeId) ===
                String(loan.loanTypeId)
            ) || existing.loanSummary?.[key];

            if (previousLoan) {
              loanSummary[key] = {
                ...currentLoan,
                collections: Number(
                  previousLoan.collections || 0
                ),
                collectionLoaded:
                  previousLoan.collectionLoaded === true,
                collectionError:
                  previousLoan.collectionError || false,
              };
            }
          });

          return {
            ...item,
            loanSummary,
            totalCollectionAmount:
              existing.totalCollectionAmount || 0,
            collectionLoaded:
              existing.collectionLoaded === true,
          };
        });
      });
      const collectionResponse = await fetch(`${Api}/api/all//dashboard-data-loanCollection`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!collectionResponse.ok) {
        throw new Error("Failed to fetch loan collection data");
      }

      const responseData = await collectionResponse.json();

      const collectionArray = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      const latestWithCollections = latestData.map((item) => {
        const collection = collectionArray.find(
          (c) =>
            String(c.jfmcTypeId) === String(item.jfmcTypeId)
        );

        const loanSummary = { ...item.loanSummary };

        const collectionSummary = Array.isArray(
          collection?.collectionSummary
        )
          ? collection.collectionSummary
          : [];

        collectionSummary.forEach((collectionItem) => {
          const collectionLoanTypeId = String(
            collectionItem.loanTypeId || ""
          );

          const matchingKey = Object.keys(loanSummary).find(
            (key) => {
              const loan = loanSummary[key];

              return (
                collectionLoanTypeId &&
                String(loan.loanTypeId || "") ===
                collectionLoanTypeId
              );
            }
          );

          if (matchingKey) {
            loanSummary[matchingKey] = {
              ...loanSummary[matchingKey],
              collections: Number(collectionItem.collected) || 0,
            };
          }
        });

        // Successful response means even zero-collection loans
        // have finished loading.
        Object.keys(loanSummary).forEach((key) => {
          loanSummary[key] = {
            ...loanSummary[key],
            collections: Number(
              loanSummary[key].collections || 0
            ),
            collectionLoaded: true,
          };
        });

        return {
          ...item,
          loanSummary,
          totalCollectionAmount: Number(
            collection?.totalCollectionAmount || 0
          ),
          collectionLoaded: true,
        };
      });

      setDashboardData(latestWithCollections);

      setCachedData(
        cacheKey || "dashboardData",
        latestWithCollections
      );

      const hasChanges = dashboardDataHasChanged(
        storedData || [],
        latestWithCollections
      );

      if (hasChanges) {
        await saveDashboardCollectionData(latestWithCollections);
      }

    } catch (error) {
      console.error(
        "Background dashboard collection refresh failed:",
        error
      );

      // Keep saved collection values visible if refresh fails.
      setDashboardData((current) =>
        current.map((item) => ({
          ...item,
          loanSummary: Object.fromEntries(
            Object.entries(item.loanSummary || {}).map(
              ([key, loan]) => [
                key,
                {
                  ...loan,

                  // Preserve previously saved values.
                  collections: Number(loan.collections || 0),

                  // Keep already-loaded collections available.
                  collectionLoaded: loan.collectionLoaded === true,

                  // Only mark an error if no saved value is available.
                  collectionError: loan.collectionLoaded !== true,
                },
              ]
            )
          ),
        }))
      );
    }
  };

  const dashboardDataHasChanged = (
    storedData,
    latestUIData
  ) => {
    // Convert UI structure into DB structure
    const latestDbData =
      latestUIData.map((item) => ({
        jfmcTypeId:
          String(item.jfmcTypeId),

        jfmcTypeName:
          item.jfmcTypeName,

        loans: Object.values(
          item.loanSummary || {}
        )
          .map((loan) => ({
            loanTypeId:
              loan.loanTypeId
                ? String(
                  loan.loanTypeId
                )
                : null,

            loanName:
              loan.loanName,

            amount:
              Number(
                loan.amount || 0
              ),

            count:
              Number(
                loan.count || 0
              ),
          }))
          .sort((a, b) =>
            a.loanName.localeCompare(
              b.loanName
            )
          ),

        totalLoanAmount:
          Number(
            item.totalLoanAmount || 0
          ),

        loanCollections: Object.values(
          item.loanSummary || {}
        )
          .map((loan) => ({
            loanTypeId:
              loan.loanTypeId
                ? String(
                  loan.loanTypeId
                )
                : null,

            loanName:
              loan.loanName,

            collected:
              Number(
                loan.collections || 0
              ),
          }))
          .sort((a, b) =>
            a.loanName.localeCompare(
              b.loanName
            )
          ),

        totalCollectionAmount:
          Number(
            item.totalCollectionAmount || 0
          ),
      }));

    const storedNormalized =
      (storedData || [])
        .map((item) => ({
          jfmcTypeId:
            String(item.jfmcTypeId),

          jfmcTypeName:
            item.jfmcTypeName,

          loans:
            (item.loans || [])
              .map((loan) => ({
                loanTypeId:
                  loan.loanTypeId
                    ? String(
                      loan.loanTypeId
                    )
                    : null,

                loanName:
                  loan.loanName,

                amount:
                  Number(
                    loan.amount || 0
                  ),

                count:
                  Number(
                    loan.count || 0
                  ),
              }))
              .sort((a, b) =>
                a.loanName.localeCompare(
                  b.loanName
                )
              ),

          totalLoanAmount:
            Number(
              item.totalLoanAmount || 0
            ),

          loanCollections:
            (item.loanCollections || [])
              .map((collection) => ({
                loanTypeId:
                  collection.loanTypeId
                    ? String(
                      collection.loanTypeId
                    )
                    : null,

                loanName:
                  collection.loanName,

                collected:
                  Number(
                    collection.collected ||
                    0
                  ),
              }))
              .sort((a, b) =>
                a.loanName.localeCompare(
                  b.loanName
                )
              ),

          totalCollectionAmount:
            Number(
              item.totalCollectionAmount ||
              0
            ),
        }))
        .sort((a, b) =>
          a.jfmcTypeId.localeCompare(
            b.jfmcTypeId
          )
        );

    const latestNormalized =
      latestDbData.sort((a, b) =>
        a.jfmcTypeId.localeCompare(
          b.jfmcTypeId
        )
      );

    return (
      JSON.stringify(storedNormalized) !==
      JSON.stringify(latestNormalized)
    );
  };
  const saveDashboardCollectionData = async (dashboardData) => {
    try {
      const config = Authorization();

      const headers = {
        "Content-Type": "application/json",
        ...config.headers,
      };
      for (const item of dashboardData) {
        const loans = Object.values(
          item.loanSummary || {}
        )
          .filter((loan) => loan.loanTypeId)
          .map((loan) => ({
            loanTypeId: String(loan.loanTypeId),
            loanName: loan.loanName || "",
            amount: Number(loan.amount || 0),
            count: Number(loan.count || 0),
          }));

        const loanCollections = Object.values(
          item.loanSummary || {}
        )
          .filter((loan) => loan.loanTypeId)
          .map((loan) => ({
            loanTypeId: String(loan.loanTypeId),
            loanName: loan.loanName || "",
            collected: Number(
              loan.collections || 0
            ),
          }));
        const response = await fetch(
          `${Api}/api/data/jfmc/${item.jfmcTypeId}`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify({
              jfmcTypeName:
                item.jfmcTypeName,

              loans,

              totalLoanAmount:
                Number(
                  item.totalLoanAmount || 0
                ),

              loanCollections,

              totalCollectionAmount:
                Number(
                  item.totalCollectionAmount || 0
                ),
            }),
          }
        );

        if (!response.ok) {
          const errorData =
            await response
              .json()
              .catch(() => ({}));

          throw new Error(
            errorData.message ||
            `Failed to save ${item.jfmcTypeName}`
          );
        }
      }


    } catch (error) {
      console.error(
        "Save Dashboard data error:",
        error
      );

      throw error;
    }
  };
  const loadVfcChart = async () => {
    const key = "circleWiseVfc";
    const cached = getCachedData(key);

    if (cached) {
      setVfcChartData(cached);
      return;
    }

    try {
      const res = await axios.get(
        `${Api}/api/all/circle-wise-vfc`,
        Authorization()
      );

      const raw = res.data.data; // ✅ FIX HERE

      const chartMap = {}; // { VFC: [], EDC: [] }

      raw.forEach(circle => {
        circle.jfmcTypes.forEach(type => {
          if (!chartMap[type.jfmcTypeName]) {
            chartMap[type.jfmcTypeName] = [];
          }

          chartMap[type.jfmcTypeName].push({
            circleId: circle.circleId,
            circleName: circle.circleName,
            totalVfcs: Number(type.totalVfcs) || 0,
          });
        });
      });

      // Sort circles alphabetically
      Object.keys(chartMap).forEach(type => {
        chartMap[type].sort((a, b) =>
          a.circleName.localeCompare(b.circleName)
        );
      });

      setVfcChartData(chartMap);
      setCachedData(key, chartMap);
    } catch (err) {
      console.error("VFC chart error:", err);
    }
  };

  const loadMemberChart = async () => {
    const key = "circleWiseMembers";
    const cached = getCachedData(key);

    if (cached) {
      setMemberChartData(cached);
      return;
    }

    try {
      const res = await axios.get(
        `${Api}/api/all/circle-wise-members`,
        Authorization()
      );

      // jfmcTypeName → chart data
      const charts = {};

      res.data.forEach(circle => {
        circle.jfmcTypes.forEach(j => {
          if (!charts[j.jfmcTypeName]) {
            charts[j.jfmcTypeName] = [];
          }

          charts[j.jfmcTypeName].push({
            circleName: circle.circleName,
            totalMembers: Number(j.totalMembers) || 0,
          });
        });
      });

      // sort circles alphabetically
      Object.keys(charts).forEach(type => {
        charts[type].sort((a, b) =>
          a.circleName.localeCompare(b.circleName)
        );
      });

      setMemberChartData(charts);
      setCachedData(key, charts);
    } catch (err) {
      console.error("Member chart error:", err);
    }
  };


  const loadLoanChart = async () => {
    const key = "circleWiseLoans";
    const cached = getCachedData(key);

    if (cached) {
      setLoanChartData(cached);

      return;
    }

    try {
      const res = await axios.get(
        `${Api}/api/all/circle-wise-loans`,
        Authorization()
      );

      const data = res.data
        .map(i => ({
          circleId: i._id.circleId,
          circleName: i.circleName,
          totalLoanAmount: Number(i.totalLoanAmount) || 0,
          totalRepaymentAmount: Number(i.totalRepaymentAmount) || 0,
          balanceAmount: Number(i.balanceAmount) || 0,
        }))
        .sort((a, b) => a.circleName.localeCompare(b.circleName));

      setLoanChartData(data);
      setCachedData(key, data);
    } catch (err) {
      console.error("Loan chart error:", err);
    }
  };
  const checkClosingBalance = async () => {
    try {
      if (CLOSING_BALANCE_EXCLUDED_ROLES.includes(role)) return;
      if (!selectedJfmcType?._id) return;

      const today = new Date();

      // 🔥 PREVIOUS MONTH
      const prevMonthDate = new Date(
        today.getFullYear(),
        today.getMonth(), // 👈 NOT +1
        0 // last day of previous month
      );

      const year = prevMonthDate.getFullYear();
      const month = prevMonthDate.getMonth() + 1; // 1-12

      const res = await axios.post(
        `${Api}/api/closing-balance/check-march`,
        {
          jfmcTypeId: selectedJfmcType._id,
          month,
          year
        },
        Authorization()
      );

      if (!res.data.isFilled) {
        setShowClosingBalance(true);
      }

    } catch (err) {
      console.error("Check closing balance failed", err);
    }
  };

  const getMergedVfcChart = () => {
    const merged = {};

    Object.keys(vfcChartData).forEach(type => {
      vfcChartData[type].forEach(item => {
        if (!merged[item.circleName]) {
          merged[item.circleName] = { circleName: item.circleName };
        }

        merged[item.circleName][type] = item.totalVfcs;
      });
    });

    return Object.values(merged);
  };

  const getMergedMemberChart = () => {
    const merged = {};

    Object.keys(memberChartData).forEach(type => {
      memberChartData[type].forEach(item => {
        if (!merged[item.circleName]) {
          merged[item.circleName] = { circleName: item.circleName };
        }

        merged[item.circleName][type] = item.totalMembers;
      });
    });

    return Object.values(merged);
  };

  const handleCloseClosingBalance = () => {
    setShowClosingBalance(false);

    // ✅ stop reopening
    sessionStorage.setItem("closingBalanceDone", "true");
  };

  const formatAmount = (amount) => {
    if (amount >= 10000000) {
      return (amount / 10000000).toFixed(2) + " Cr"; // Crores
    } else if (amount >= 100000) {
      return (amount / 100000).toFixed(2) + " L"; // Lakhs
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(2) + " K"; // Thousands
    }
    return amount; // Return as is if less than 1000
  };
  const handleOpenClosingBalance = () => {
    setShowClosingBalance(true);
  };
  const handleOpenRangeApproval = () => {
    setShowRangeApproval(true);
  };
  // const handleCloseClosingBalance = () => {
  //   setShowClosingBalance(false);
  // };
  const handleCloseRangeApproval = () => {
    setShowRangeApproval(false);
  };

  const handleOpenDivisionApproval = () => setShowDivisionApproval(true);
  const handleCloseDivisionApproval = () => setShowDivisionApproval(false);

  const handleOpenCircleApproval = () => setShowCircleApproval(true);
  const handleCloseCircleApproval = () => setShowCircleApproval(false);


  const handleOpenStateView = () => setShowStateView(true);
  const handleCloseStateView = () => setShowStateView(false);

  const CHART_COLORS = {
    green: "#2e7d32",
    blue: "#1e88e5",
    orange: "#f9a825",
    purple: "#6a1b9a",
    red: "#c62828",
    indigo: "#3730a3",
    violet: "#6d28d9",
    teal: "#0f766e",
    cyan: "#155e75",
    pink: "#9d174d",
    amber: "#b45309"
  };

  // Stable order → auto assignment
  const COLOR_ORDER = ["green", "blue", "orange", "purple", "red", "indigo", "violet", "teal", "cyan", "pink", "amber"];
  const LOAN_COLOR_ORDER = ["indigo", "violet", "teal", "cyan", "pink", "amber"];
  const ICONS = [
    "fas fa-university",
    "fas fa-coins",
    "fas fa-sync",
    "fas fa-percentage",
    "fas fa-file-invoice",
    "fas fa-chart-line"
  ];
  // Lighten helper for Members
  const lightenColor = (hex, amount = 50) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, (num >> 16) + amount);
    const g = Math.min(255, ((num >> 8) & 0xff) + amount);
    const b = Math.min(255, (num & 0xff) + amount);
    return `rgb(${r}, ${g}, ${b})`;
  };
  const getThemeColor = (type, variant = "COUNT") => {
    let hash = 0;

    for (let i = 0; i < type.length; i++) {
      hash = type.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % COLOR_ORDER.length;

    const baseColor = CHART_COLORS[COLOR_ORDER[index]];

    return variant === "MEMBERS"
      ? lightenColor(baseColor, 50)
      : baseColor;
  };

  const shouldShowServiceCharge = (() => {
    // ❌ Excluded roles never see it
    if (SERVICE_CHARGE_EXCLUDED_ROLES.includes(role)) {
      return false;
    }

    // ❌ Already shown once in this session
    const alreadyShown = sessionStorage.getItem(
      "serviceChargeShown"
    );

    if (alreadyShown === "true") {
      return false;
    }

    // ✅ Eligible + first time
    return true;
  })();
  const handleServiceChargeComplete = () => {
    sessionStorage.setItem("serviceChargeShown", "true");
    setServiceCheckDone(true);
  };
  useEffect(() => {
    // Unblock dashboard if payment is disabled globally or charge not needed
    if (!shouldShowServiceCharge || process.env.REACT_APP_PAYMENT_ENABLED !== "true") {
      setServiceCheckDone(true);
    }
  }, [shouldShowServiceCharge]);

  return (
    <div>

      {process.env.REACT_APP_PAYMENT_ENABLED === "true" && shouldShowServiceCharge && !serviceCheckDone && (
        <ServiceChargePaymentCheck
          onComplete={handleServiceChargeComplete}
        />
      )}

      {serviceCheckDone && (
        <>
          {loading && <Preloader loading={loading} />}
          <div className="page-header">
            <h3 className="page-title">
              <span className="page-title-icon bg-gradient-primary text-white mr-2">
                <i className="mdi mdi-home" style={{ color: "#fff" }}></i>
              </span>
              Dashboard
            </h3>
            <div style={{ minWidth: 200 }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd-MMM-yyyy"
                className="form-control"
                placeholderText="Start Date"
              />
            </div>
            {permissions.includes("ViewReports") && (
              <div className="jfmc-switch">
                <button
                  className="btn btn-success"
                  onClick={() => navigate("/reports")}
                >
                  Reports
                </button>
              </div>
            )}
            {!CLOSING_BALANCE_EXCLUDED_ROLES.includes(role) && (
              <div className="jfmc-switch">
                <button
                  className="btn btn-success"
                  onClick={handleOpenClosingBalance}
                >
                  Add Bank Balance
                </button>
              </div>
            )}

          </div>
          <div className="dashboard-sections">
            {[...dashboardData]
              .sort((a, b) =>
                String(a.jfmcTypeName || "").localeCompare(
                  String(b.jfmcTypeName || ""),
                  undefined,
                  { sensitivity: "base" }
                )
              )
              .map((d) => (
                <DashboardDataCard
                  key={d.jfmcTypeId}
                  data={d}
                  formatAmount={formatAmount}
                  loanColorOrder={LOAN_COLOR_ORDER}
                  icons={ICONS}
                />
              ))}
          </div>

          {/* <button
        className="btn btn-primary"
        onClick={() => setShowQrModal(true)}
      >
        Pay Annual VFC payment
      </button>
      <span>{"<--Click here to pay annual payment! Already paid? Ignore it."}</span> */}


          {permissions.includes("ApproveDeclarations") && (
            <div className="approval-portal mt-4">
              <div className="section-header">
                <h4 className="section-header-title">
                  Approval Control Center
                </h4>
              </div>


              <div className="approval-grid">
                {permissions.includes("VFCApproval") && (
                  <div className="approval-card card-green" onClick={handleOpenRangeApproval}>
                    <div className="card-header">
                      <span className="card-title">Range Approval</span>
                      <div className="card-icon icon-green">
                        <i className="mdi mdi-map-marker-check"></i>
                      </div>
                    </div>
                    <div className="card-desc">
                      Approve VFC-level data submissions
                    </div>
                  </div>
                )}

                {permissions.includes("DivisionApproval") && (
                  <div className="approval-card card-blue" onClick={handleOpenDivisionApproval}>
                    <div className="card-header">
                      <span className="card-title">Division Approval</span>
                      <div className="card-icon icon-blue">
                        <i className="mdi mdi-office-building"></i>
                      </div>
                    </div>
                    <div className="card-desc">
                      Review Range approvals and forward for Circle
                    </div>
                  </div>
                )}

                {permissions.includes("CircleApproval") && (
                  <div className="approval-card card-orange" onClick={handleOpenCircleApproval}>
                    <div className="card-header">
                      <span className="card-title">Circle Approval</span>
                      <div className="card-icon icon-orange">
                        <i className="mdi mdi-circle-multiple-outline"></i>
                      </div>
                    </div>
                    <div className="card-desc">
                      Validate divisional reports before state view
                    </div>
                  </div>
                )}

                {permissions.includes("StateStatus") && (
                  <div className="approval-card card-purple" onClick={handleOpenStateView}>
                    <div className="card-header">
                      <span className="card-title">State View</span>
                      <div className="card-icon icon-purple">
                        <i className="mdi mdi-earth"></i>
                      </div>
                    </div>
                    <div className="card-desc">
                      Monitor and finalize statewide approvals
                    </div>
                  </div>
                )}

                {!permissions.some(p =>
                  ["VFCApproval", "DivisionApproval", "CircleApproval", "StateStatus"].includes(p)
                ) && (
                    <p className="text-center text-muted mb-0">
                      <i className="mdi mdi-lock-outline"></i> No Approval Permissions Assigned
                    </p>
                  )}
              </div>
            </div>
          )}


          {/* ── Analytics Section Header ── */}
          {(Object.keys(vfcChartData).length > 0 || Object.keys(memberChartData).length > 0 || loanChartData.length > 0) && (
            <div className="dash-section-header mt-4">
              <i className="mdi mdi-chart-bar dash-section-icon" />
              <span>Circle-wise Analytics</span>
            </div>
          )}


          <div className="row">

            {/* LEFT → Bank Balance */}
            <div className="col-lg-6 grid-margin">
              <div className="card dash-chart-card h-100">
                <div className="card-header dash-chart-header">
                  <span className="dash-chart-icon dash-icon-green">
                    <i className="mdi mdi-bank" />
                  </span>
                  <div>
                    <div className="dash-chart-title">Circle-wise Bank Balance</div>
                    <div className="dash-chart-sub">Combined view</div>
                  </div>
                </div>

                <div className="card-body dash-chart-body">
                  <ViewBankBalance />
                </div>
              </div>
            </div>

            <div className="col-lg-6 d-flex flex-column">

              {/* VFC CARD */}
              <div className="card dash-chart-card mb-3 flex-fill d-flex flex-column">
                <div className="card-header dash-chart-header">
                  <span className="dash-chart-icon dash-icon-green">
                    <i className="mdi mdi-office-building" />
                  </span>
                  <div>
                    <div className="dash-chart-title">JFMCs in Circles</div>
                    <div className="dash-chart-sub">Combined view</div>
                  </div>
                </div>

                <div className="card-body dash-chart-body flex-grow-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getMergedVfcChart()}>
                      <XAxis dataKey="circleName" angle={-45} textAnchor="end" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(vfcChartData).map(type => (
                        <Bar key={type} dataKey={type} fill={getThemeColor(type)} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* MEMBERS CARD */}
              <div className="card dash-chart-card flex-fill d-flex flex-column">
                <div className="card-header dash-chart-header">
                  <span className="dash-chart-icon dash-icon-blue">
                    <i className="mdi mdi-account-group" />
                  </span>
                  <div>
                    <div className="dash-chart-title">Members in Circles</div>
                    <div className="dash-chart-sub">Combined view</div>
                  </div>
                </div>

                <div className="card-body dash-chart-body flex-grow-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getMergedMemberChart()}>
                      <XAxis dataKey="circleName" angle={-45} textAnchor="end" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(memberChartData).map(type => (
                        <Bar key={type} dataKey={type} fill={getThemeColor(type, "MEMBERS")} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          </div>

          <div className="row">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  <RangeTableView />
                </div>
              </div>
            </div>
          </div>

          <ClosingBalance
            open={showClosingBalance}
            onClose={handleCloseClosingBalance}
            jfmcTypeId={selectedJfmcType}
          />
          <RangeApproval
            open={showRangeApproval}
            onClose={handleCloseRangeApproval}
          />
          <DivisionApproval
            open={showDivisionApproval}
            onClose={handleCloseDivisionApproval}
          />
          <CircleApproval
            open={showCircleApproval}
            onClose={handleCloseCircleApproval}
          />
          {/* Modal */}
          <CircleStatusModal
            open={showStateView}
            onClose={handleCloseStateView}
            Api={Api}
          />
          {error && <p className="text-danger">{error}</p>}
        </>
      )}
    </div >

  );
};

export default Dashboard;
